import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import JsonQuery from 'json-query';
import { isValid } from 'date-fns';
import { datePicker, iconRender } from '../';
import { useLocalization } from '../../hooks';
import { convertUTCToLocalDate } from '../../WidgetUtils';
import { DateFilterOptions } from './';
import 'react-datepicker/dist/react-datepicker.css';

interface CustomDatePickerInputProps extends DateFilterOptions {
    value?: string;
    onClick?: () => void;
    startDate?: any;
    endDate?: any;
    isStartDate?: boolean;
    isEndDate?: boolean;
    startDateErrorMessage?: string;
    endDateErrorMessage?: string;
    clearFilters?: boolean;
    staticDataTabIndex?: number;
}

const CustomDatePickerInput: React.FC<CustomDatePickerInputProps> = forwardRef(
    (props, ref: any) => {
        const { value, onClick, isStartDate, isEndDate, startDate, endDate, ...rest } = props;

        // Function to render the icon based on the provided options
        const renderIcon = () => (
            <iconRender.IconRender
                showSpan={true}
                className={`custom-date-picker-icon-${
                    rest?.datePickerIconPosition === 'Left' ? 'left' : 'right'
                }`}
                iconType={rest?.datePickerIconType}
                iconName={rest.datePickerIcon}
                iconClassName={''}
                imageUrl={rest?.datePickerImage}
                imageClassName={'custom-date-picker-image'}
                imageAlt={'dropdown-toggle-open'}
                staticDataTabIndex={rest.staticDataTabIndex}
            />
        );

        // Function to render the icon based on the provided options
        const renderErrorIcon = () => (
            <iconRender.IconRender
                showSpan={true}
                className={`error-message-icon-${
                    rest?.errorIconPosition === 'Left' ? 'left' : 'right'
                }`}
                iconType={rest?.errorIconType}
                iconName={rest?.errorIcon}
                iconClassName={''}
                imageUrl={rest?.errorImageUrl}
                imageClassName={'error-message-image'}
                imageAlt={'error-message'}
                staticDataTabIndex={rest.staticDataTabIndex}
            />
        );

        const hasStartDateError =
            startDate && startDate > endDate && isStartDate && rest?.startDateErrorMessage;
        const hasEndDateError =
            endDate && startDate > endDate && isEndDate && rest?.endDateErrorMessage;
        const hasError = hasStartDateError || hasEndDateError;

        return (
            <div className="custom-date-picker">
                <button
                    className={`custom-date-picker-input ${
                        hasError ? 'custom-date-picker-input-error' : ''
                    }`}
                    onClick={onClick}
                    ref={ref}
                >
                    {rest?.datePickerIconPosition === 'Left' ? renderIcon() : null}
                    <span className="custom-date-picker-value">{value}</span>
                    {rest?.datePickerIconPosition === 'Right' ? renderIcon() : null}
                </button>
                <span className="custom-date-picker-error-message">
                    {hasStartDateError && (
                        <>
                            {rest?.errorIconPosition === 'Left' ? renderErrorIcon() : null}
                            {isStartDate && rest?.startDateErrorMessage}
                            {isEndDate && rest?.endDateErrorMessage}
                            {rest?.errorIconPosition === 'Right' ? renderErrorIcon() : null}
                        </>
                    )}
                    {hasEndDateError && (
                        <>
                            {rest?.errorIconPosition === 'Left' ? renderErrorIcon() : null}
                            {isEndDate && rest?.endDateErrorMessage}
                            {rest?.errorIconPosition === 'Right' ? renderErrorIcon() : null}
                        </>
                    )}
                </span>
            </div>
        );
    }
);

interface DateFilterProps {
    data: any;
    onChangeDateRange: (minDate: any, maxDate: any) => void;
    staticDataTabIndex?: number;
}

const DateFilter: React.FC<DateFilterOptions & DateFilterProps> = (props) => {
    const { data, startDatelabel, endDatelabel, dateFormat, ...rest } = props;
    const [startDate, setStartDate] = useState<any>(null);
    const [endDate, setEndDate] = useState<any>(null);
    const [minDate, setMinDate] = useState<any>(null);
    const [maxDate, setMaxDate] = useState<any>(null);
    const i18n = useLocalization();

    // Extract the dates from the provided data using json-query
    const dates = useMemo(
        () => JsonQuery(rest?.dataKey || '', { data }).value || [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rest?.dataKey]
    );

    // Update the start and end date when the dates data changes
    useEffect(() => {
        if (dates?.length > 0) {
            let minDate = new Date(dates?.[0]);
            let maxDate = new Date(dates?.[0]);

            dates?.forEach((date: string) => {
                const currentDate = new Date(date);
                if (isValid(currentDate)) {
                    if (currentDate < minDate) {
                        minDate = currentDate;
                    }
                    if (currentDate > maxDate) {
                        maxDate = currentDate;
                    }
                }
            });

            if (isValid(minDate) && isValid(maxDate)) {
                setMinDate(minDate);
                setStartDate(minDate);
                setMaxDate(maxDate);
                setEndDate(maxDate);
            }
        }
    }, [dates]);

    // Call the onChangeDateRange callback when the start or end date changes
    useEffect(() => {
        if (
            startDate &&
            isValid(startDate) &&
            endDate &&
            isValid(endDate) &&
            !(startDate > endDate)
        ) {
            rest.onChangeDateRange(startDate, endDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    useEffect(() => {
        if (rest?.clearFilters) {
            setMinDate('');
            setStartDate('');
            setMaxDate('');
            setEndDate('');
            rest.onChangeDateRange('', '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rest?.clearFilters]);

    const limitedDates = useMemo(
        () => JsonQuery(rest?.limitDatekeySelection || '', { data }).value || [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rest?.limitDatekeySelection]
    );

    const filteredDateArray: Date[] = [];

    if (limitedDates?.length > 0) {
        limitedDates.forEach((date: string) => {
            const currentDate = convertUTCToLocalDate(new Date(date));
            if (isValid(currentDate)) {
                filteredDateArray.push(currentDate);
            }
        });
    }

    return (
        <div className="date-filter-container">
            <div className="start-date-container">
                <span tabIndex={rest.staticDataTabIndex}>{i18n.format(startDatelabel, data)}</span>
                <datePicker.DatePicker
                    id="startDateInput"
                    minDate={rest.limitStartDateMinToMinDate ? minDate : null}
                    maxDate={
                        rest.limitStartDateMaxToCurrentDate
                            ? new Date()
                            : rest?.limitStartDateMaxToMaxDate
                              ? maxDate
                              : null
                    }
                    selected={startDate}
                    onChange={(date: any) => setStartDate(date)}
                    dateFormat={dateFormat}
                    customInput={
                        <CustomDatePickerInput
                            {...props}
                            isStartDate={true}
                            startDate={startDate}
                            endDate={endDate}
                            startDateErrorMessage={i18n.format(rest?.startDateErrorMessage, data)}
                        />
                    }
                    closeOnScroll
                    formatWeekDay={rest.numberOfLettersForTheDays as any}
                    filteredDateArray={filteredDateArray}
                />
            </div>
            <div className="end-date-container">
                <span tabIndex={rest.staticDataTabIndex}>{i18n.format(endDatelabel, data)}</span>
                <datePicker.DatePicker
                    id="endDateInput"
                    selected={endDate}
                    minDate={rest.limitEndDateMinToStartDate ? startDate : null}
                    maxDate={
                        rest?.limitEndDateMaxToCurrentDate
                            ? new Date()
                            : rest?.limitEndDateMaxToMaxDate
                              ? maxDate
                              : null
                    }
                    onChange={(date: any) => setEndDate(date)}
                    dateFormat={dateFormat}
                    customInput={
                        <CustomDatePickerInput
                            {...props}
                            isEndDate={true}
                            startDate={startDate}
                            endDate={endDate}
                            endDateErrorMessage={i18n.format(rest?.endDateErrorMessage, data)}
                        />
                    }
                    closeOnScroll
                    formatWeekDay={rest.numberOfLettersForTheDays as any}
                    filteredDateArray={filteredDateArray}
                />
            </div>
        </div>
    );
};

export default DateFilter;
